import { AnimationService } from '@/services/animations'
import { audioService } from '@/services/audio'
import { getSVGPoint, isInsideCookie, vibrate, vibrateBreak } from '@/utils/dom'
import type { GameState, Point } from '@/types'

const TAPS_TO_BREAK = 1 // Number of clicks needed to crack
const POST_CRACK_COOLDOWN_MS = 900

// Click-based progress system
const PROGRESS_TO_CRACK = 100
// Removed unused POINTS_PER_CLICK constant

// Feedback based on click speed (time between clicks)
const FAST_CLICK_THRESHOLD = 300 // ms - very fast clicking = "Perfect!"
const GOOD_CLICK_THRESHOLD = 600 // ms - good clicking pace = "Good!"
const NORMAL_CLICK_THRESHOLD = 1200 // ms - normal pace = "Nice!"

export class CookieInteraction {
  private cookieSVG: SVGSVGElement
  private animations: AnimationService
  private clickCount = 0
  private cracked = false
  private tapHud: HTMLElement
  // Removed unused tapDots property
  private tapHint: HTMLElement
  
  // Click-based progress system properties
  private progress = 0
  private lastClickTime = 0
  private feedbackElement: HTMLElement | null = null
  private progressBar: HTMLElement | null = null
  private interactionLocked = false
  private resetHint: HTMLElement | null = null
  private postCrackLockTimeout: number | null = null
  private cracksLeft = 0

  constructor(cookieSVG: SVGSVGElement) {
    this.cookieSVG = cookieSVG
    this.animations = new AnimationService(cookieSVG)
    this.tapHud = this.getExistingTapHUD()
    // Removed tapDots initialization - no longer needed
    this.tapHint = this.tapHud.querySelector('#tapHint')!
    this.resetHint = document.getElementById('resetHint')

    this.setupFeedbackSystem()
    this.setupEventListeners()
  }

  private setupFeedbackSystem(): void {
    // Create feedback text element
    const feedback = document.createElement('div')
    feedback.id = 'clickFeedback'
    feedback.className = 'click-feedback'
    feedback.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 24px;
      font-weight: bold;
      color: #FFD700;
      text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
      pointer-events: none;
      opacity: 0;
      z-index: 1000;
    `
    document.querySelector('.cookie-container')?.appendChild(feedback)
    this.feedbackElement = feedback
  }

  private getExistingTapHUD(): HTMLElement {
    const tapHud = document.getElementById('tapHud')
    if (!tapHud) {
      throw new Error('Tap HUD element not found in HTML')
    }
    
    // Replace dots with progress bar
    const tapDots = tapHud.querySelector('#tapDots')
    if (tapDots) {
      tapDots.innerHTML = `
        <div class="progress-container">
          <div class="progress-bar" id="progressBar">
            <div class="progress-fill"></div>
          </div>
        </div>
      `
      this.progressBar = tapDots.querySelector('#progressBar')
    }
    
    tapHud.hidden = true
    return tapHud
  }

  private setupEventListeners(): void {
    this.cookieSVG.addEventListener('click', this.handleClick.bind(this))
    this.cookieSVG.addEventListener('pointerdown', () => this.cookieSVG.classList.add('pressed'))
    this.cookieSVG.addEventListener('pointerup', () => this.cookieSVG.classList.remove('pressed'))
  }

  private async handleClick(event: MouseEvent): Promise<void> {
    if (this.interactionLocked) return

    const point = getSVGPoint(this.cookieSVG, event)
    if (!isInsideCookie(point)) return

    this.interactionLocked = true

    if (this.cracked) {
      this.requestReset()
      return
    }

    // Single tap feedback
    this.clickCount++
    const currentTime = Date.now()
    const feedback = this.getFeedbackFromClickSpeed(currentTime)

    this.showFeedback(feedback, point)
    this.updateProgress()
    this.lastClickTime = currentTime

    this.animations.ripple(point)
    await this.animations.wiggleCookie(2)
    audioService.playTick(this.clickCount)
    vibrate(1)

    // Final crack sequence with enhanced effects
    audioService.playCrack()
    vibrateBreak()
    this.animations.screenShake()
    this.crack()

    // Enhanced particle burst timed with the crack every tap
    this.animations.crackBurst(100, 110)

    this.startPostCrackCooldown()
  }

  private getFeedbackFromClickSpeed(currentTime: number): 'perfect' | 'good' | 'nice' | 'slow' {
    if (this.lastClickTime === 0) {
      // First click is always "nice"
      return 'nice'
    }
    
    const timeSinceLastClick = currentTime - this.lastClickTime
    
    if (timeSinceLastClick <= FAST_CLICK_THRESHOLD) {
      return 'perfect'
    } else if (timeSinceLastClick <= GOOD_CLICK_THRESHOLD) {
      return 'good'
    } else if (timeSinceLastClick <= NORMAL_CLICK_THRESHOLD) {
      return 'nice'
    } else {
      return 'slow'
    }
  }

  private showFeedback(feedback: string, point: Point): void {
    if (!this.feedbackElement) return

    const feedbackTexts = {
      perfect: 'Perfect!',
      good: 'Good!',
      nice: 'Nice!',
      slow: 'Slow...'
    }

    const colors = {
      perfect: '#FFD700',
      good: '#90EE90',
      nice: '#87CEEB',
      slow: '#FFA500'
    }

    this.feedbackElement.textContent = feedbackTexts[feedback as keyof typeof feedbackTexts]
    this.feedbackElement.style.color = colors[feedback as keyof typeof colors]
    
    // Position feedback text at click location
    const cookieContainer = document.querySelector('.cookie-container')
    if (cookieContainer) {
      const containerRect = cookieContainer.getBoundingClientRect()
      
      // Convert SVG coordinates to screen coordinates
      const screenX = point.x * (containerRect.width / 200) // SVG is 200 units wide
      const screenY = point.y * (containerRect.height / 220) // SVG is 220 units tall
      
      // Position relative to container
      this.feedbackElement.style.left = `${screenX}px`
      this.feedbackElement.style.top = `${screenY}px`
      this.feedbackElement.style.transform = 'translate(-50%, -50%)'
    }
    
    // Animate feedback text
    this.animations.showFeedbackText(this.feedbackElement, feedback)
  }

  private updateProgress(): void {
    // Simple progress: each click adds equal progress
    this.progress = (this.clickCount / TAPS_TO_BREAK) * PROGRESS_TO_CRACK
    this.updateProgressBar()
  }

  private updateProgressBar(): void {
    if (!this.progressBar) return

    const fillElement = this.progressBar.querySelector('.progress-fill') as HTMLElement
    if (fillElement) {
      const percentage = (this.progress / PROGRESS_TO_CRACK) * 100
      fillElement.style.width = `${percentage}%`
      
      // Color based on progress
      if (percentage >= 80) {
        fillElement.style.backgroundColor = '#FFD700'
      } else if (percentage >= 50) {
        fillElement.style.backgroundColor = '#90EE90'
      } else {
        fillElement.style.backgroundColor = '#FFA500'
      }
    }
  }



  private crack(): void {
    this.cracked = true
    this.updateTapHUD()

    this.hideResetHint()

    // Prevent nav from hiding during the split animation
    document.body.classList.add('cookie-opening')

    this.animations.openCookie()

    // Remove guard after the animation finishes
    setTimeout(() => document.body.classList.remove('cookie-opening'), 800)

    // Emit event for the app to handle
    window.dispatchEvent(new CustomEvent('cookieCracked'))
  }

  private startPostCrackCooldown(): void {
    this.cookieSVG.style.pointerEvents = 'none'
    this.cookieSVG.style.cursor = 'default'

    if (this.postCrackLockTimeout) {
      clearTimeout(this.postCrackLockTimeout)
    }

    this.postCrackLockTimeout = window.setTimeout(() => {
      this.cookieSVG.style.pointerEvents = 'auto'
      this.cookieSVG.style.cursor = 'pointer'
      this.interactionLocked = false
      this.showResetHint()
    }, POST_CRACK_COOLDOWN_MS)
  }

  private updateTapHUD(): void {
    const shouldHide = this.cracked
    this.tapHud.hidden = shouldHide

    if (shouldHide) {
      this.tapHint.textContent = ''
      return
    }

    // Update message based on progress
    const progressPercent = Math.round((this.progress / PROGRESS_TO_CRACK) * 100)

    if (this.clickCount === 0) {
      this.tapHint.textContent = 'Tap to crack!'
    } else if (this.clickCount >= TAPS_TO_BREAK) {
      this.tapHint.textContent = 'Cracking...'
    } else {
      this.tapHint.textContent = `${progressPercent}%`
    }

    this.updateProgressBar()
  }

  updateFromGameState(state: GameState): void {
    const shouldHide = state.cracksLeft <= 0 || this.cracked
    this.tapHud.hidden = shouldHide
    this.cracksLeft = state.cracksLeft

    // Only update pointer events if cookie is not cracked
    // When cracked, cooldown handling manages pointer events
    if (!this.cracked) {
      this.cookieSVG.style.pointerEvents = state.cracksLeft === 0 ? 'none' : 'auto'
      this.cookieSVG.style.opacity = state.cracksLeft === 0 ? '0.45' : '1'
    }

    if (state.cracksLeft <= 0) {
      this.hideResetHint()
    }

    if (!shouldHide) {
      this.updateTapHUD()
    }
  }

  async reset(): Promise<void> {
    if (this.postCrackLockTimeout) {
      clearTimeout(this.postCrackLockTimeout)
      this.postCrackLockTimeout = null
    }

    await this.animations.resetCookie()
    this.cracked = false
    this.clickCount = 0
    this.progress = 0
    this.lastClickTime = 0
    this.interactionLocked = false

    // Reset feedback element
    if (this.feedbackElement) {
      this.feedbackElement.style.opacity = '0'
    }

    this.updateTapHUD()
    this.hideResetHint()

    // Re-enable interactions
    this.cookieSVG.style.pointerEvents = 'auto'
    this.cookieSVG.style.cursor = 'pointer'
  }

  isCracked(): boolean {
    return this.cracked
  }

  private showResetHint(): void {
    if (!this.resetHint || this.cracksLeft <= 0) return

    this.resetHint.hidden = false
    this.resetHint.classList.add('visible')
  }

  private hideResetHint(): void {
    if (!this.resetHint) return

    this.resetHint.hidden = true
    this.resetHint.classList.remove('visible')
  }

  private requestReset(): void {
    this.hideResetHint()
    window.dispatchEvent(new CustomEvent('resetCookieRequested'))
  }
}
